import { Add, ArrowBack } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Button, IconButton, styled, Tooltip, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AppDispatch } from '../../../redux/store';
import {
  getProject,
  getProjectConfig,
  insertProjectKpiQuestion,
  processQuestion,
  syncProjectDefaultKPIs,
  updateAnalysisQuestionFeedback,
} from '../../../services/dealGPT';
import { StyledTab, StyledTabs } from '../../../theme/CustomComponents';
import type { ProjectConfigQuestion, ProjectRunAnalysisQuestion } from '../../../Types/dealGPT';
import {
  AnalysisKPIQuestionType,
  IndexType,
  ProcessReason,
  ProjectConfigView,
} from '../../../Types/enums';
import { FeedbackInterface } from '../../../Types/feedback';
import { Folder } from '../../../Types/folder';
import { checkUnauthorizedError } from '../../../util/checkUnauthorizedError';
import DealGPTContainer from '../../DealGPT/ViewModels/DealGPTContainer';
import LoadingMask from '../../LoadingMask';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import { useAuth } from '../../Providers/AuthProvider';
import KPI from '../PhaseTwo/KPIs/KPI';
import { setPageTitle } from '../utils';
import KpiQuestion from '../Views/ConfigureProject/KpiQuestion';
import ProjectConfig from '../Views/ConfigureProject/ProjectConfig';

const ProjectContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
  marginBottom: '50px',
  gap: '24px',
});

const initialValues = {
  investmentThesis: '',
  keyQuestions: [
    {
      projectId: '',
      question: '',
      deleted: false,
    },
  ],
  kpiQuestions: [
    {
      projectId: '',
      question: '',
      deleted: false,
      kpiType: AnalysisKPIQuestionType.TEXT,
    },
  ],
  projectRunId: '',
};

const Section = styled('div')({
  '&:not(:first-of-type)': {
    borderTop: '1px solid #E9E9E9',
    paddingTop: '20px',
  },
});

const DealGPTConfigureProjectContainer = ({
  folder,
  selectedView,
}: {
  folder?: Folder;
  selectedView: ProjectConfigView;
}): JSX.Element => {
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();
  const { isAdmin, isDealGPTAdmin } = useAuth();
  const [projectName, setProjectName] = useState<string>('');
  const [fetchConfig, setFetchConfig] = useState<boolean>(true);
  const [view, setView] = useState<ProjectConfigView>(selectedView);
  const [keyQuestions, setKeyQuestions] = useState<ProjectConfigQuestion[]>(
    initialValues.keyQuestions
  );
  const [investmentThesis, setInvestmentThesis] = useState<string>(initialValues.investmentThesis);
  const [projectRunId, setProjectRunId] = useState<string>(initialValues.projectRunId);
  const [questionToAdd, setQuestionToAdd] = useState<ProjectConfigQuestion>();
  const [kpiQuestions, setKpiQuestions] = useState<ProjectRunAnalysisQuestion[]>([]);
  const [expandAllKpis, setExpandAllKpis] = useState<boolean>(false);
  const [isLoadingThis, setIsLoadingThis] = useState<boolean>(true);
  const [isProcessingDefaultKPIs, setIsProcessingDefaultKPIs] = useState<boolean>(false);
  const [indexToUse, setIndexToUse] = useState<IndexType>(IndexType.OPENAI);
  const analytics = useAnalytics();

  const initialQuestion = useMemo(
    () => ({
      id: undefined,
      question: '',
      projectId: folder?.projectId ?? '',
      deleted: false,
      isNew: true,
    }),
    [folder?.projectId]
  );

  useEffect(() => {
    const fetchProjectConfig = async () => {
      try {
        if (folder?.projectId && fetchConfig) {
          const projectResponse = await getProject(folder.projectId, isDealGPTAdmin);

          if (projectResponse.id) {
            setProjectName(projectResponse.name);
            setIndexToUse(projectResponse.indexToUse);
          }

          const config = await getProjectConfig(folder.projectId);
          setKeyQuestions(
            config.keyQuestions.length === 0 ? [initialQuestion] : config.keyQuestions
          );
          setInvestmentThesis(config.investmentThesis ?? '');
          setKpiQuestions(config.kpiQuestions);
          setProjectRunId(config.projectRunId);
          setFetchConfig(false);
          setIsLoadingThis(false);

          if (config.kpiQuestions.length === 0) {
            setQuestionToAdd({ ...initialValues.kpiQuestions[0] });
          }
        }
      } catch (error) {
        setIsLoadingThis(false);
        enqueueSnackbar(`Failed to fetch project`, {
          variant: 'error',
        });

        if (checkUnauthorizedError(error as Error)) {
          navigate('/intellio-advantage', { replace: true });
        }
      }
    };

    fetchProjectConfig();
  }, [
    folder?.projectId,
    fetchConfig,
    navigate,
    isDealGPTAdmin,
    dispatch,
    indexToUse,
    initialQuestion,
  ]);

  // Update Page title with current project name
  useEffect(() => {
    if (folder?.projectId && projectName) {
      setPageTitle({ projectId: folder.projectId, projectName });
    }
  }, [folder?.projectId, projectName, selectedView]);

  const handleKpiSubmitted = async (question: ProjectConfigQuestion) => {
    if (folder?.projectId) {
      setIsLoadingThis(true);
      const inserted = await insertProjectKpiQuestion(
        { ...question, projectId: folder.projectId },
        projectRunId,
        kpiQuestions.length + 1
      );

      if (inserted) {
        try {
          await processQuestion(inserted, ProcessReason.PROCESS_THIS);
          setQuestionToAdd(undefined);
          setFetchConfig(true);
        } catch (error) {
          enqueueSnackbar('Failed to apply changes. Please try again later.', {
            variant: 'error',
          });
        } finally {
          setIsLoadingThis(false);
        }
      }
    }
  };

  const handleKPIFeedbackSubmit = async (
    feedback: FeedbackInterface,
    projectAnalysisQuestionId: string
  ) => {
    try {
      analytics.event(
        AnalyticsEvent.WM_IA_PROJECT_CONFIG_KPI_FEEDBACK,
        {
          id: projectAnalysisQuestionId,
          value: feedback.toString(),
        },
        {
          projectId: folder?.projectId,
          questionId: projectAnalysisQuestionId,
          thumbsUp: feedback.thumbsUp ?? undefined,
          otherFeedback: feedback.otherFeedback ?? undefined,
          suggestedFeedback: feedback.suggestedFeedback.toString() ?? undefined,
        }
      );
      await updateAnalysisQuestionFeedback(projectAnalysisQuestionId, feedback);

      setKpiQuestions(
        (prevQuestions) =>
          prevQuestions?.map((question) =>
            question.id === projectAnalysisQuestionId
              ? {
                  ...question,
                  analysisQuestionFeedback: feedback,
                }
              : question
          ) || []
      );
    } catch (error) {
      enqueueSnackbar('Failed to submit feedback. Please try again later.', {
        variant: 'error',
      });
      return;
    }
  };

  const handleDefaultKPIs = useCallback(async () => {
    analytics.event(
      AnalyticsEvent.WM_IA_PROJECT_CONFIG_KPI_DEFAULT_ADD,
      {
        id: folder?.projectId,
      },
      {
        projectId: folder?.projectId,
      }
    );
    if (!folder?.projectId) return;

    setIsProcessingDefaultKPIs(true);
    await syncProjectDefaultKPIs(folder.projectId);

    // Fetch the updated KPIs after 30 seconds delay
    setTimeout(() => {
      setIsProcessingDefaultKPIs(false);
      setFetchConfig(true);
      enqueueSnackbar(
        'Default KPIs have been added successfully! They are still processing, check back later to see the results.',
        { variant: 'success' }
      );
    }, 30000);
  }, [folder?.projectId, analytics]);

  return (
    <DealGPTContainer>
      <div
        style={{
          overflow: 'auto',
          width: '100%',
          height: '100%',
          maxHeight: 'calc(100vh - var(--banner-height, 0px))',
        }}
      >
        <ProjectContainer>
          <LoadingMask isLoading={isLoadingThis} />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              sx={{ height: '24px', width: '24px', marginRight: '10px' }}
              onClick={() => {
                navigate(`/folder/${folder?.id}`);
              }}
            >
              <ArrowBack sx={{ color: '#092B49' }} />
            </IconButton>
            <Typography sx={{ fontSize: '36px', fontWeight: '700' }}>{projectName}</Typography>
          </div>
          <Typography variant="h5" sx={{ fontWeight: '400' }}>
            Project Configuration / Project KPIs
          </Typography>

          <StyledTabs
            value={view}
            onChange={(_, newView: ProjectConfigView) => {
              let viewUrl = '';

              if (newView === ProjectConfigView.KPI) {
                viewUrl = '/kpi';
              }
              setView(newView);
              navigate(`/folder/${folder?.id}/configure${viewUrl}`);
            }}
            sx={{ borderBottom: '1px solid #C4C4C4' }}
          >
            <StyledTab value={ProjectConfigView.CONFIG} label={'Project Configuration'} />
            <StyledTab value={ProjectConfigView.KPI} label={'Project KPIs'} />
          </StyledTabs>
          {view === ProjectConfigView.CONFIG && (
            <ProjectConfig
              keyQuestions={keyQuestions}
              investmentThesis={investmentThesis}
              projectId={folder?.projectId ?? ''}
            />
          )}
          {view === ProjectConfigView.KPI && (
            <div>
              <div style={{ display: 'flex', marginBottom: '16px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                  }}
                >
                  <Tooltip
                    title={<div style={{ whiteSpace: 'pre-line' }}>{'Add another question'}</div>}
                  >
                    <span>
                      <Button
                        variant="outlined"
                        disabled={questionToAdd !== undefined}
                        onClick={() => setQuestionToAdd({ ...initialValues.kpiQuestions[0] })}
                        sx={{ width: '100px' }}
                      >
                        <Add /> Add
                      </Button>
                    </span>
                  </Tooltip>
                  {isAdmin && (
                    <LoadingButton
                      variant="outlined"
                      onClick={handleDefaultKPIs}
                      loading={isProcessingDefaultKPIs}
                      sx={{ width: '150px' }}
                    >
                      Add Default KPIs
                    </LoadingButton>
                  )}
                </div>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                  {kpiQuestions.length > 0 && (
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#1979CD',
                        cursor: 'pointer',
                        marginBottom: '16px',
                        alignSelf: 'flex-end',
                      }}
                      onClick={() => {
                        setExpandAllKpis(!expandAllKpis);
                      }}
                    >
                      {expandAllKpis ? 'Collapse all' : 'Expand all'}
                    </Typography>
                  )}
                </div>
              </div>
              {questionToAdd && (
                <KpiQuestion
                  question={questionToAdd}
                  handleDelete={() => {
                    setQuestionToAdd(undefined);
                  }}
                  handleUpdate={handleKpiSubmitted}
                />
              )}
              {kpiQuestions.map((kpi, index) => (
                <Section key={index} sx={{ marginTop: '16px' }}>
                  <KPI
                    kpi={kpi}
                    setIsLoadingThis={setIsLoadingThis}
                    setIsLoadingApplyChanges={setIsLoadingThis}
                    refetchQuestions={() => {
                      setIsLoadingThis(true);
                      setFetchConfig(true);
                    }}
                    expandAll={expandAllKpis}
                    handleFeedbackSubmit={handleKPIFeedbackSubmit}
                    handleValidateFindingsClick={() => {}}
                  />
                </Section>
              ))}
            </div>
          )}
        </ProjectContainer>
      </div>
    </DealGPTContainer>
  );
};

export default DealGPTConfigureProjectContainer;
